
import type { Action, Reducer } from 'redux';
import { languageList, ZLLanguage } from '../helpers/lang';
import { setLanguage } from '../helpers/i18n';
import { IAppThunkAction } from './';
import BrazeEvents from '../helpers/braze';

export interface IInternationalisationState {
	selectedLanguage: ZLLanguage;
}

// Module-private -- only dispatched by setAppLanguage thunk
const SET_LOCALE: 'SET_LOCALE' = 'SET_LOCALE';

interface ISetLocaleAction {
	type: typeof SET_LOCALE;
	selectedLanguage: ZLLanguage;
}

const defaultLanguage: ZLLanguage = languageList[0]; // en-US

const undefinedState: IInternationalisationState = {
	selectedLanguage: defaultLanguage,
};

// Module-private -- starts false per JS runtime (app instance).
// Set true after first setAppLanguage call, stays true for the SPA session.
// Only resets on hard refresh / new tab.
// Not in Redux state so it can never be serialised to localStorage.
let languageInitialised = false;

export type KnownInternationalisationActions = ISetLocaleAction;

export const actionCreators = {
	// Single entry point for all language changes -- boot, UI picker, URL sync.
	// Updates: i18next, moment, Redux, Braze.
	// URL sync is handled separately by useUrlLanguageSync hook.
	setAppLanguage:
		(locale: string): IAppThunkAction<KnownInternationalisationActions> =>
		(dispatch, getState) => {
			const lower = locale.toLowerCase();
			const lang = languageList.find(
				(lng) => lng.code.toLowerCase() === lower || lng.momentLocale.toLowerCase() === lower
			);
			if (!lang) {
				console.log('[setAppLanguage] no match for:', locale);
				return;
			}

			const { selectedLanguage } = getState().intl;

			// After first init, skip if language hasn't changed
			if (languageInitialised && selectedLanguage.code === lang.code) {
				console.log('[setAppLanguage] skip (already initialised, same language):', lang.momentLocale);
				return;
			}

			console.log('[setAppLanguage] changing:', selectedLanguage.momentLocale, '->', lang.momentLocale, '| initialised:', languageInitialised);

			// 1. i18next + moment
			setLanguage(lang);

			// 2. Redux state
			dispatch({ type: SET_LOCALE, selectedLanguage: lang });
			languageInitialised = true;

			// 3. Braze analytics
			BrazeEvents.SetUserLanguage(lang.momentLocale);
		},
};

export const reducer: Reducer<IInternationalisationState> = (
	state: IInternationalisationState | undefined,
	incomingAction: Action
): IInternationalisationState => {
	const action: KnownInternationalisationActions = incomingAction as KnownInternationalisationActions;
	switch (action.type) {
		case SET_LOCALE: {
			return {
				...state,
				selectedLanguage: action.selectedLanguage,
			};
		}
		default:
			if (state === undefined) return undefinedState;
			return state;
	}
};
